// covered-by: qa/rickrubin/tests/api/ask-puntt-share.spec.ts
import { z } from "zod";

import { demoBaselineSchema } from "../commons/enterprise";

/** Per-chat ROI facts for the meeting_roi skill (not stored on Enterprise). */
export const askPunttRoiSessionSchema = demoBaselineSchema;
export type AskPunttRoiSession = z.infer<typeof askPunttRoiSessionSchema>;

// Workflow types — built-in skills + custom_<uuid> for enterprise custom workflows
export const askPunttWorkflowTypeSchema = z.union([
  z.enum([
    "compliance_review",
    "artwork_review",
    "compare",
    "review",
    "brief_creation",
    "claim_generation",
    "general",
    // @deprecated Legacy "chat as a project" workflow. Superseded by the
    // first-class AskPunttProject entity (a chat now links to a project via
    // chat.projectId + projectChatKind, with workflowType "general"). Retained
    // because the live carrie FE still filters on it; removal ships with the
    // FE migration.
    "project",
  ]),
  z.string().startsWith("custom_"),
]);
export type AskPunttWorkflowType = z.infer<typeof askPunttWorkflowTypeSchema>;

// Source result in search progress
export const askPunttSourceResultSchema = z.object({
  title: z.string(),
  url: z.string().optional(),
  type: z.string().optional(), // e.g. "document", "ticket", "regulation"
  // Optional opaque ID — used by rembrandt's "Create Ticket" search-progress
  // entries to thread ticket IDs through to the chat (so per-ticket logic
  // like multi-asset checklist labels can resolve titles by id).
  id: z.string().optional(),
});
export type AskPunttSourceResult = z.infer<typeof askPunttSourceResultSchema>;

// Search progress for a single source
export const askPunttSearchProgressSchema = z.object({
  source: z.string(), // e.g. "Puntt Regulatory Database", "Danone's Claims Data", "Wrike"
  status: z.enum(["searching", "done", "error"]),
  resultCount: z.number().optional(),
  results: z.array(askPunttSourceResultSchema).optional(),
});
export type AskPunttSearchProgress = z.infer<
  typeof askPunttSearchProgressSchema
>;

// Alternative claim suggestion
export const askPunttAlternativeSchema = z.object({
  riskLevel: z.enum(["low", "medium", "high"]),
  claim: z.string(),
  why: z.string().optional(),
  precedent: z.string().optional(),
  status: z.string().optional(),
  approvalPath: z.string().optional(),
  tradeOff: z.string().optional(),
});
export type AskPunttAlternative = z.infer<typeof askPunttAlternativeSchema>;

// Source citation
export const askPunttSourceCitationSchema = z.object({
  label: z.string(), // e.g. "Puntt Regulatory Database", "EUR Lex", "ASA"
  url: z.string().optional(),
  type: z.enum(["regulatory", "internal", "external"]).optional(),
});
export type AskPunttSourceCitation = z.infer<
  typeof askPunttSourceCitationSchema
>;

// Structured content for rich AI responses
export const askPunttStructuredContentSchema = z.object({
  riskLevel: z.enum(["low", "medium", "high"]).optional(),
  confidence: z.enum(["low", "medium", "high"]).optional(),
  shortAnswer: z.string().optional(),
  regulatoryAnalysis: z.string().optional(),
  alternatives: z.array(askPunttAlternativeSchema).optional(),
  sources: z.array(askPunttSourceCitationSchema).optional(),
  timeSaved: z
    .object({
      summary: z.string(), // e.g. "2-3 Weeks"
      breakdown: z.array(
        z.object({
          week: z.string(),
          description: z.string(),
        }),
      ),
    })
    .optional(),
});
export type AskPunttStructuredContent = z.infer<
  typeof askPunttStructuredContentSchema
>;

// Attachment (S3 reference)
export const askPunttAttachmentSchema = z.object({
  key: z.string(),
  source: z.string().url(),
  type: z.enum(["image", "video", "file"]),
  name: z.string().optional(),
});
export type AskPunttAttachment = z.infer<typeof askPunttAttachmentSchema>;

// Agent step (thinking, tool call, tool result) for real-time activity log
export const askPunttAgentStepSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("thinking"),
    content: z.string(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("tool_call"),
    tool: z.string(),
    displayName: z.string(),
    statusName: z.string().optional(),
    args: z.record(z.unknown()).optional(),
    description: z.string().optional(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("tool_result"),
    tool: z.string(),
    displayName: z.string(),
    statusName: z.string().optional(),
    summary: z.string(),
    resultCount: z.number().optional(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("text"),
    content: z.string(),
    roundIndex: z.number(),
    timestamp: z.string(),
  }),
]);
export type AskPunttAgentStep = z.infer<typeof askPunttAgentStepSchema>;

// Content block types for block-based message rendering
export const askPunttContentBlockSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    content: z.string(),
  }),
  z.object({
    type: z.literal("tool_activity"),
    stepRange: z.tuple([z.number(), z.number()]),
  }),
]);
export type AskPunttContentBlock = z.infer<typeof askPunttContentBlockSchema>;

// Canvas block types for rich structured output
export const askPunttCanvasBlockSchema: z.ZodType = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("heading"),
      level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      text: z.string(),
    }),
    z.object({ type: z.literal("paragraph"), text: z.string() }),
    z.object({
      type: z.literal("badge"),
      label: z.string(),
      color: z.enum(["green", "amber", "red", "blue", "gray"]),
      size: z.enum(["sm", "md", "lg"]).optional(),
    }),
    z.object({
      type: z.literal("card"),
      title: z.string(),
      subtitle: z.string().optional(),
      badge: z.record(z.unknown()).optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("grid"),
      columns: z.number(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("table"),
      headers: z.array(z.string()),
      rows: z.array(z.array(z.string())),
      highlightRules: z.record(z.string()).optional(),
    }),
    z.object({
      type: z.literal("list"),
      ordered: z.boolean(),
      items: z.array(z.record(z.unknown())),
    }),
    z.object({ type: z.literal("divider") }),
    z.object({
      type: z.literal("metric"),
      label: z.string(),
      value: z.string(),
      trend: z
        .enum([
          "up-positive",
          "up-negative",
          "down-positive",
          "down-negative",
          "neutral",
          "up",
          "down",
        ])
        .optional(),
    }),
    z.object({
      type: z.literal("expandable"),
      title: z.string(),
      summary: z.string().optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("section"),
      id: z.string(),
      title: z.string(),
      editable: z.boolean().optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("source_link"),
      title: z.string(),
      url: z.string(),
      linkType: z.string().optional(),
    }),
    z.object({
      type: z.literal("chart"),
      chartType: z.enum(["bar", "pie", "line"]),
      data: z.record(z.unknown()),
    }),
    z.object({
      type: z.literal("callout"),
      variant: z.enum(["info", "warning", "success", "error"]),
      text: z.string(),
    }),
    z.object({
      type: z.literal("file_preview"),
      url: z.string(),
      fileName: z.string(),
      fileType: z.enum(["pdf", "xlsx", "xls", "docx", "doc", "pptx", "ppt"]),
      pageCount: z.number().optional(),
    }),
  ]),
);
export type AskPunttCanvasBlock = z.infer<typeof askPunttCanvasBlockSchema>;

// Canvas content wrapping blocks
export const askPunttCanvasContentSchema = z.object({
  outputType: z.enum([
    "risk_assessment",
    "brief_document",
    "comparison",
    "report",
    "visualization",
    "custom",
  ]),
  blocks: z.array(z.record(z.unknown())),
  rawHtml: z.string().nullable().optional(),
  exportFormats: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type AskPunttCanvasContent = z.infer<typeof askPunttCanvasContentSchema>;

// Structured question option (for multi-question forms)
export const askPunttQuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
  isUpload: z.boolean().optional(),
});
export type AskPunttQuestionOption = z.infer<
  typeof askPunttQuestionOptionSchema
>;

// Structured question (used by AskUserQuestion tool)
export const askPunttUserQuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(askPunttQuestionOptionSchema),
  multiSelect: z.boolean().optional(),
  allowFiles: z.boolean().optional(),
});
export type AskPunttUserQuestion = z.infer<typeof askPunttUserQuestionSchema>;

// Project stage status
export const askPunttProjectStageSchema = z.object({
  name: z.string(),
  status: z.enum(["pending", "active", "done", "blocked", "needs_review"]),
  color: z.enum(["green", "amber", "red", "gray"]),
});
export type AskPunttProjectStage = z.infer<typeof askPunttProjectStageSchema>;

// Project reviewer routing
export const askPunttProjectReviewerSchema = z.object({
  name: z.string(),
  initials: z.string(),
  role: z.string(),
  mode: z.enum(["Parallel", "After"]),
  status: z.enum(["active", "pending", "done", "needs_review"]),
});
export type AskPunttProjectReviewer = z.infer<
  typeof askPunttProjectReviewerSchema
>;

// Project forecast data
export const askPunttProjectForecastSchema = z.object({
  cycleTimeDays: z.number(),
  vsTargetDays: z.number(),
  confidence: z.number().min(0).max(100),
});
export type AskPunttProjectForecast = z.infer<
  typeof askPunttProjectForecastSchema
>;

export const askPunttProjectPlanStepSchema = z.object({
  title: z.string(),
  description: z.string(),
  status: z.enum(["pending", "active", "done"]),
});
export type AskPunttProjectPlanStep = z.infer<
  typeof askPunttProjectPlanStepSchema
>;

// Per-ticket finding summary (populated after review completion)
export const askPunttTicketFindingSchema = z.object({
  ticketId: z.string(),
  ticketTitle: z.string(),
  commentCount: z.number(),
  topFindings: z.array(z.string()),
  severity: z.enum(["clean", "minor", "major"]),
  /**
   * Per-asset risk rollup, surfaced as a chip on each asset card in
   * `ProjectStatusPane`. Distinct from `severity`: severity is the
   * highest finding severity on the ticket, riskScore is the
   * three-bucket label the demo wants Bill (and any other buyer) to
   * see ("these three came back low / medium / high").
   *
   * Phase 1 of the 3-tier asset flow plan. Optional because rembrandt-
   * produced project data does not (yet) populate it.
   */
  riskScore: z.enum(["low", "medium", "high"]).optional(),
});
export type AskPunttTicketFinding = z.infer<typeof askPunttTicketFindingSchema>;

// Compliance verdict tallies — bucket counts for claim-level review outcomes.
// Bucket names mirror ComplianceVerdict in commons/ticket.ts.
// `non_compliant` is the internal storage key (UI renders "Non-compliant").
export const askPunttComplianceTallySchema = z.object({
  approved: z.number().default(0),
  derivative: z.number().default(0),
  non_compliant: z.number().default(0),
  unsure: z.number().default(0),
  total: z.number().default(0),
});
export type AskPunttComplianceTally = z.infer<
  typeof askPunttComplianceTallySchema
>;

export const askPunttTicketComplianceSchema =
  askPunttComplianceTallySchema.extend({
    ticketId: z.string(),
  });
export type AskPunttTicketCompliance = z.infer<
  typeof askPunttTicketComplianceSchema
>;

export const askPunttComplianceSummarySchema =
  askPunttComplianceTallySchema.extend({
    ticketsWithCompliance: z.number(),
    ticketsReviewed: z.number(),
    ticketsExpected: z.number(),
    isComplete: z.boolean(),
    generatedAt: z.string(),
  });
export type AskPunttComplianceSummary = z.infer<
  typeof askPunttComplianceSummarySchema
>;

// Full project data (lives on chat + message level)
/**
 * @deprecated Legacy chat-as-project payload. Superseded by the first-class
 * AskPunttProject entity (packages/schemas/src/christo/askPunttProject.ts).
 * Retained because the live carrie FE still renders it; removal ships with the
 * FE migration.
 */
export const askPunttProjectDataSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  markets: z.array(z.string()),
  projectType: z.string(),
  stages: z.array(askPunttProjectStageSchema),
  routing: z.array(askPunttProjectReviewerSchema),
  forecast: askPunttProjectForecastSchema,
  aiRecommendation: z.string().optional(),
  targetDeadline: z.string().optional(),
  completedAt: z.string().optional(),
  confidence: z.number().min(0).max(100).optional(),
  intent: z.string().optional(),
  planSteps: z.array(askPunttProjectPlanStepSchema).optional(),
  reviewScope: z.enum(["full", "quick"]).optional(),
  findingsSummary: z.string().optional(),
  findingsSummaryGeneratedAt: z.string().optional(),
  ticketFindings: z.array(askPunttTicketFindingSchema).optional(),
  ticketIds: z.array(z.string()).optional(),
  ticketCompliance: z.array(askPunttTicketComplianceSchema).optional(),
  complianceSummary: askPunttComplianceSummarySchema.optional(),
});
export type AskPunttProjectData = z.infer<typeof askPunttProjectDataSchema>;

// Message
export const askPunttMessageSchema = z.object({
  _id: z.string(),
  chatId: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  status: z.enum(["pending", "completed"]).optional(),
  structuredContent: askPunttStructuredContentSchema.optional(),
  activeSkill: z.string().optional(),
  canvasContent: askPunttCanvasContentSchema.optional(),
  searchProgress: z.array(askPunttSearchProgressSchema).optional(),
  agentSteps: z.array(askPunttAgentStepSchema).optional(),
  contentBlocks: z.array(askPunttContentBlockSchema).nullable().optional(),
  attachments: z.array(askPunttAttachmentSchema).optional(),
  viewTicketId: z.string().optional(),
  paneLabels: z
    .object({
      context: z.string().optional(),
      artwork: z.string().optional(),
    })
    .optional(),
  suggestedResponses: z.array(z.string()).nullable().optional(),
  userQuestions: z.array(askPunttUserQuestionSchema).nullable().optional(),
  /** @deprecated Legacy chat-as-project data; superseded by AskPunttProject. */
  projectData: askPunttProjectDataSchema.nullable().optional(),
  streamingContent: z.string().nullable().optional(),
  canvasLoading: z.boolean().nullable().optional(),
  /** @deprecated Legacy chat-as-project streaming flag; see AskPunttProject. */
  projectLoading: z.boolean().nullable().optional(),
  canvasChangedSectionIds: z.array(z.string()).optional(),
  feedback: z.enum(["thumbs_up", "thumbs_down"]).nullable().optional(),
  /** Present on user-role messages in multi-member project shared chats. */
  userId: z.string().optional(),
  /**
   * Actor ids mentioned in a feed user message: member `_id`s plus the
   * `"puntt"` sentinel for the AI agent. Display + attribution only — does
   * not affect whether the AI workflow runs.
   */
  mentions: z.array(z.string()).optional(),
  createdAt: z.string(),
});
export type AskPunttMessage = z.infer<typeof askPunttMessageSchema>;

export const reviewChecklistItemSchema = z.object({
  label: z.string(),
  sub: z.string(),
});
export type ReviewChecklistItem = z.infer<typeof reviewChecklistItemSchema>;

// Agent eval result — populated by
// services/rembrandt/scripts/askpuntt_agent_eval.py. One snapshot per chat,
// overwritten on each run. Mirrored in the Mongoose model at
// packages/commons-node/src/models/askPunttChat.ts so strict-mode Mongoose
// preserves the field on updates.
export const askPunttAgentEvalMetricSchema = z.object({
  score: z.number().nullable(),
  pass: z.boolean().nullable(),
  reason: z.string().nullable(),
});
export type AskPunttAgentEvalMetric = z.infer<
  typeof askPunttAgentEvalMetricSchema
>;

export const askPunttAgentEvalExchangeSchema = z.object({
  userInputPreview: z.string(),
  // Headline metric. Combines this user's actual request (primary signal)
  // with the active skill's promise (secondary frame) into a single score
  // for "did the AI do what the user wanted?"
  // Optional so legacy agentEval snapshots (pre-rename, containing
  // skillQuality but no goalAchievement) still parse cleanly during the
  // re-eval rollout; populated on every fresh run.
  goalAchievement: askPunttAgentEvalMetricSchema.nullable().optional(),
  // Legacy field name. Kept optional so historical snapshots written before
  // the GoalAchievement rename still parse. New runs do NOT populate this.
  skillQuality: askPunttAgentEvalMetricSchema.nullable().optional(),
  taskCompletion: askPunttAgentEvalMetricSchema.nullable(),
  stepEfficiency: askPunttAgentEvalMetricSchema.nullable(),
  // Populated only when the exchange's chat actually created tickets and we
  // can evaluate whether the agent assembled the right asset_context. Null
  // for chat-only exchanges (no ticket → no signal to judge).
  ticketContext: askPunttAgentEvalMetricSchema.nullable(),
  ticketTitles: z.array(z.string()).default([]),
  // Live (non-deleted) review comments.
  reviewCommentCount: z.number().default(0),
  // All review comments including soft-deleted — denominator for dismissalRate.
  totalCommentCount: z.number().default(0),
  // 1 - (live / total). Closest organic proxy for a thumbs-down. Null when
  // there are no tickets or no comments — we don't dilute the average with
  // "no signal" cases.
  dismissalRate: z.number().nullable().optional(),
  error: z.string().nullable(),
});
export type AskPunttAgentEvalExchange = z.infer<
  typeof askPunttAgentEvalExchangeSchema
>;

export const askPunttAgentEvalSchema = z.object({
  evaluatedAt: z.union([z.string(), z.date()]),
  runId: z.string().nullable().optional(),
  exchanges: z.array(askPunttAgentEvalExchangeSchema),
});
export type AskPunttAgentEval = z.infer<typeof askPunttAgentEvalSchema>;

// Compact summary of a user we've shared a chat with (or the chat's owner,
// when the requester is a shared user). Used by the share dialog and the
// "Shared with me" sidebar attribution.
export const askPunttChatUserSummarySchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  email: z.string().optional(),
  avatar: z.string().nullable().optional(),
});
export type AskPunttChatUserSummary = z.infer<
  typeof askPunttChatUserSummarySchema
>;

// Chat
export const askPunttChatSchema = z.object({
  _id: z.string(),
  title: z.string(),
  workflowType: askPunttWorkflowTypeSchema,
  enterpriseId: z.string(),
  userId: z.string(),
  enabledConnectors: z.array(z.string()).optional(),
  detectedSkill: z.string().nullable().optional(),
  ticketIds: z.array(z.string()).optional(),
  reviewStatus: z.string().nullable().optional(),
  reviewChecklist: z.array(reviewChecklistItemSchema).optional(),
  /** @deprecated Legacy chat-as-project data; superseded by AskPunttProject. */
  projectData: askPunttProjectDataSchema.nullable().optional(),
  // First-class project link. projectId == null ⇒ standalone chat (legacy
  // behavior). projectChatKind distinguishes the project's shared conversation
  // from members' private personal chats.
  projectId: z.string().nullable().optional(),
  projectChatKind: z.enum(["shared", "personal"]).nullable().optional(),
  roiSession: askPunttRoiSessionSchema.optional(),
  agentEval: askPunttAgentEvalSchema.nullable().optional(),
  // Populated only when the requester has cross-user read access (MG admins
  // viewing other users' chats) OR is a shared user viewing the owner's
  // chat. Omitted for the chat owner's own chats.
  ownerName: z.string().optional(),
  ownerEmail: z.string().optional(),
  // Whether the requester is the owner ("owner") or accessed via the
  // allowList ("shared"). Used by the FE to gate the Share / Delete
  // affordances.
  role: z.enum(["owner", "shared"]).optional(),
  // Hydrated list of users this chat has been shared with. Populated by the
  // share dialog and chat-history responses. Order is insertion order.
  allowList: z.array(askPunttChatUserSummarySchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AskPunttChat = z.infer<typeof askPunttChatSchema>;

// --- Request/Response Schemas ---

// List chats
export const askPunttChatScopeSchema = z.enum(["mine", "shared", "all"]);
export type AskPunttChatScope = z.infer<typeof askPunttChatScopeSchema>;

export const listAskPunttChatsRequestSchema = z.object({
  query: z.object({
    limit: z.coerce.number().optional().default(20),
    offset: z.coerce.number().optional().default(0),
    // mine = chats owned by the requester (default, current behavior).
    // shared = chats where the requester is in allowList.
    // all = union of mine + shared, with role: "owner" | "shared" per row.
    scope: askPunttChatScopeSchema.optional().default("mine"),
  }),
});
export const listAskPunttChatsResponseSchema = z.object({
  chats: z.array(
    askPunttChatSchema
      .pick({
        _id: true,
        title: true,
        workflowType: true,
        userId: true,
        enabledConnectors: true,
        projectData: true,
        updatedAt: true,
      })
      // Populated for MG callers viewing other users' chats AND for shared
      // users viewing chats they were granted access to.
      .extend({
        ownerName: z.string().optional(),
        ownerEmail: z.string().optional(),
        createdAt: z.string().optional(),
        role: z.enum(["owner", "shared"]).optional(),
        sharedAt: z.string().optional(),
      }),
  ),
  total: z.number().default(0),
});
export type ListAskPunttChatsResponse = z.infer<
  typeof listAskPunttChatsResponseSchema
>;

// Get chat history
export const getAskPunttChatHistoryRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
});
export const getAskPunttChatHistoryResponseSchema = z.object({
  chat: askPunttChatSchema,
  messages: z.array(askPunttMessageSchema),
});
export type GetAskPunttChatHistoryResponse = z.infer<
  typeof getAskPunttChatHistoryResponseSchema
>;

// Create chat
export const createAskPunttChatRequestSchema = z.object({
  body: z.object({
    message: z.string(),
    workflowType: askPunttWorkflowTypeSchema.optional().default("general"),
    attachments: z.array(askPunttAttachmentSchema).optional().default([]),
    enabledConnectors: z.array(z.string()).optional().default([]),
    // Optional project link — the single create path also creates chats inside
    // a project. projectId attaches the new chat to a project; projectChatKind
    // marks it as the project's shared conversation or a personal chat.
    projectId: z.string().optional(),
    projectChatKind: z.enum(["shared", "personal"]).optional(),
  }),
});
export const createAskPunttChatResponseSchema = z.object({
  chat: askPunttChatSchema,
  userMessage: askPunttMessageSchema,
  assistantMessage: askPunttMessageSchema.optional(),
  workflowId: z.string().optional(),
});
export type CreateAskPunttChatResponse = z.infer<
  typeof createAskPunttChatResponseSchema
>;

// Send message
export const sendAskPunttMessageRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
  body: z.object({
    message: z.string(),
    attachments: z.array(askPunttAttachmentSchema).optional().default([]),
    sectionContext: z.object({ id: z.string(), title: z.string() }).optional(),
    mentions: z.array(z.string()).optional(),
  }),
});
export const sendAskPunttMessageResponseSchema = z.object({
  userMessage: askPunttMessageSchema,
  // workflowId is omitted when the demo enterprise returns a scripted
  // canned response (no Temporal workflow is started); see
  // services/christo/app/handlers/catalyst/demoAskPunttResponses.ts.
  workflowId: z.string().optional(),
  assistantMessage: askPunttMessageSchema.optional(),
});
export type SendAskPunttMessageResponse = z.infer<
  typeof sendAskPunttMessageResponseSchema
>;

// Submit feedback
export const submitAskPunttFeedbackRequestSchema = z.object({
  params: z.object({ chatId: z.string(), messageId: z.string() }),
  body: z.object({ feedback: z.enum(["thumbs_up", "thumbs_down"]) }),
});
export const submitAskPunttFeedbackResponseSchema = z.object({
  success: z.boolean(),
});

// Delete chat
export const deleteAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
});
export const deleteAskPunttChatResponseSchema = z.object({
  success: z.boolean(),
});
export type DeleteAskPunttChatResponse = z.infer<
  typeof deleteAskPunttChatResponseSchema
>;

// Abort message (stop generating)
export const abortAskPunttMessageRequestSchema = z.object({
  params: z.object({ chatId: z.string(), messageId: z.string() }),
});
export const abortAskPunttMessageResponseSchema = z.object({
  message: askPunttMessageSchema,
});
export type AbortAskPunttMessageResponse = z.infer<
  typeof abortAskPunttMessageResponseSchema
>;

// Project action (override stage / complete project)
/**
 * @deprecated Legacy chat-as-project action (operates on chat.projectData).
 * Superseded by the first-class AskPunttProject entity. Retained because the
 * live carrie FE still calls /ask-puntt/chats/:chatId/project/action; removal
 * ships with the FE migration.
 */
export const updateAskPunttProjectActionRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z.object({
    action: z.enum(["override_stage", "complete_project"]),
    stageName: z.string().optional(),
  }),
});

// Share chat with one or more enterprise users (owner-only).
export const shareAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z.object({
    userIds: z.array(z.string()).min(1).max(50),
  }),
});
export const shareAskPunttChatResponseSchema = z.object({
  allowList: z.array(askPunttChatUserSummarySchema),
});
export type ShareAskPunttChatResponse = z.infer<
  typeof shareAskPunttChatResponseSchema
>;

// Unshare chat (remove a user from allowList) (owner-only).
export const unshareAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string(), userId: z.string() }),
});
export const unshareAskPunttChatResponseSchema = z.object({
  allowList: z.array(askPunttChatUserSummarySchema),
});
export type UnshareAskPunttChatResponse = z.infer<
  typeof unshareAskPunttChatResponseSchema
>;

// Search enterprise users by name/email (server-side regex). Used by the
// share dialog and the rembrandt ShareChatTool. Scoped to the requester's
// enterprise via JWT.
export const searchEnterpriseUsersRequestSchema = z.object({
  query: z.object({
    q: z.string().min(2).max(120),
    limit: z.coerce.number().int().min(1).max(25).optional().default(10),
  }),
});
export const searchEnterpriseUsersResponseSchema = z.object({
  users: z.array(askPunttChatUserSummarySchema),
});
export type SearchEnterpriseUsersResponse = z.infer<
  typeof searchEnterpriseUsersResponseSchema
>;
